/**
 * Models & Providers API adapter
 * Fetches LLM models and providers from the database
 */

import type { LLMModel, LLMProvider } from '../types/results';

const API_BASE_URL = import.meta.env.VITE_API_URL !== undefined
  ? import.meta.env.VITE_API_URL
  : 'http://localhost:3001';

export interface ModelWithProvider extends LLMModel {
  provider: {
    id: string;
    name: string;
    description: string | null;
  };
}

export interface ModelsResponse {
  models: ModelWithProvider[];
  count: number;
}

export interface ProvidersResponse {
  providers: LLMProvider[];
  count: number;
}

export interface ProviderModelsResponse {
  providerId: string;
  models: LLMModel[];
  count: number;
}

// V1 API response types
interface V1ModelsResponse {
  data: {
    models: Record<string, Array<{
      id: string;
      name: string;
      provider_id: string;
      display_name: string;
      description: string | null;
      max_context_length: number;
      supports_streaming: boolean;
      supports_functions: boolean;
      cost_per_1k_input_tokens: number;
      cost_per_1k_output_tokens: number;
      is_active: boolean;
      capabilities: string[] | null;
    }>>;
    total_models: number;
    providers: string[];
  };
}

interface V1ProvidersResponse {
  data: Array<{
    id: string;
    name: string;
    display_name: string;
    description: string | null;
    base_url: string | null;
    is_active: boolean;
    rate_limits: Record<string, unknown> | null;
    model_count: number;
  }>;
}

/**
 * Fetch all providers from v1 API
 */
async function fetchProvidersMap(): Promise<Map<string, { id: string; name: string; description: string | null }>> {
  const response = await fetch(`${API_BASE_URL}/api/v1/models/providers`);

  if (!response.ok) {
    throw new Error(`Failed to fetch providers: ${response.status} ${response.statusText}`);
  }

  const json: V1ProvidersResponse = await response.json();
  const providersMap = new Map();

  json.data.forEach(provider => {
    providersMap.set(provider.id, {
      id: provider.id,
      name: provider.display_name || provider.name,
      description: provider.description
    });
  });

  return providersMap;
}

/**
 * Fetch all available models with provider info from v1 API
 */
export async function fetchModels(): Promise<ModelsResponse> {
  // Fetch providers first to populate provider info
  const providersMap = await fetchProvidersMap();

  // Fetch models from v1 API
  const response = await fetch(`${API_BASE_URL}/api/v1/models`);

  if (!response.ok) {
    throw new Error(`Failed to fetch models: ${response.status} ${response.statusText}`);
  }

  const json: V1ModelsResponse = await response.json();

  // Flatten grouped models structure into array with provider info
  const modelsArray: ModelWithProvider[] = [];

  Object.entries(json.data.models).forEach(([providerId, models]) => {
    const providerInfo = providersMap.get(providerId) || {
      id: providerId,
      name: providerId,
      description: null
    };

    models.forEach(model => {
      modelsArray.push({
        id: model.id,
        name: model.name,
        displayName: model.display_name,
        description: model.description,
        providerId: model.provider_id,
        maxContextLength: model.max_context_length,
        supportsStreaming: model.supports_streaming,
        supportsFunctions: model.supports_functions,
        costPer1kInputTokens: model.cost_per_1k_input_tokens,
        costPer1kOutputTokens: model.cost_per_1k_output_tokens,
        isActive: model.is_active,
        capabilities: model.capabilities,
        provider: providerInfo
      });
    });
  });

  return {
    models: modelsArray,
    count: json.data.total_models
  };
}

/**
 * Fetch all providers
 */
export async function fetchProviders(): Promise<ProvidersResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/models/providers`);

  if (!response.ok) {
    throw new Error(`Failed to fetch providers: ${response.status} ${response.statusText}`);
  }

  const json: V1ProvidersResponse = await response.json();

  const providers: LLMProvider[] = json.data.map(provider => ({
    id: provider.id,
    name: provider.name,
    displayName: provider.display_name,
    description: provider.description,
    baseUrl: provider.base_url,
    isActive: provider.is_active,
    rateLimits: provider.rate_limits
  }));

  return {
    providers,
    count: providers.length
  };
}

/**
 * Fetch models for a specific provider
 */
export async function fetchProviderModels(providerId: string): Promise<ProviderModelsResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/models?provider=${providerId}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch models for provider ${providerId}: ${response.status} ${response.statusText}`);
  }

  const json: V1ModelsResponse = await response.json();

  // Extract models for the specific provider
  const providerModels = json.data.models[providerId] || [];

  const models: LLMModel[] = providerModels.map(model => ({
    id: model.id,
    name: model.name,
    displayName: model.display_name,
    description: model.description,
    providerId: model.provider_id,
    maxContextLength: model.max_context_length,
    supportsStreaming: model.supports_streaming,
    supportsFunctions: model.supports_functions,
    costPer1kInputTokens: model.cost_per_1k_input_tokens,
    costPer1kOutputTokens: model.cost_per_1k_output_tokens,
    isActive: model.is_active,
    capabilities: model.capabilities
  }));

  return {
    providerId,
    models,
    count: models.length
  };
}
